import { useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { CustomKnowledgeRecord } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import isEqual from 'lodash-es/isEqual';
import { useQueries } from 'react-query';
import { UsersApi } from '@frontend/api-users';
import { useTranslation } from '@frontend/i18n';
import { Button, Text } from '@frontend/design-system';
import { useAIRCustomKnowledgeStore } from '../../../../stores/air-settings.store';
import { KnowledgeFormSection } from '../../common/knowledge-form-section';
import { CustomKnowledgeSidePanel } from './custom-knowledge-side-panel';

type Props = {
  locationId: string;
};

export const MAX_CUSTOM_KNOWLEDGE_ITEMS = 5;

export const CustomKnowledgeForm = ({ locationId }: Props) => {
  const { t } = useTranslation('ai-receptionist-settings');

  const { draftSettings, initialSettings, mutateDraft } = useAIRCustomKnowledgeStore([
    'draftSettings',
    'initialSettings',
    'mutateDraft',
  ]);

  const [editItem, setEditItem] = useState<CustomKnowledgeRecord | null>(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const settingsData = useMemo(() => {
    return draftSettings[locationId] || {};
  }, [draftSettings, locationId]);

  const initialData = useMemo(() => {
    return initialSettings[locationId] || {};
  }, [initialSettings, locationId]);

  const customKnowledgeList = useMemo(() => {
    return settingsData.records || [];
  }, [settingsData]);

  const userIds = useMemo(() => {
    return Array.from(new Set(customKnowledgeList.map((item) => item.updatedBy).filter((id): id is string => !!id)));
  }, [customKnowledgeList]);

  const userQueries = useQueries(
    userIds.map((id) => ({
      queryKey: ['user', id],
      queryFn: () => UsersApi.list.getUserProfileByUserID(id),
    }))
  );

  const usersMap = useMemo(() => {
    const map: Record<string, string> = {};
    userQueries.forEach((query, index) => {
      const id = userIds[index];
      const profile = query.data;
      if (profile) {
        map[id] = `${profile.FirstName} ${profile.LastName}`.trim();
      } else {
        map[id] = id;
      }
    });
    return map;
  }, [userQueries, userIds]);

  const initialList = useMemo(() => {
    return initialData.records || [];
  }, [initialData]);

  const isLimitReached = customKnowledgeList.length >= MAX_CUSTOM_KNOWLEDGE_ITEMS;

  const handleAddClick = () => {
    if (isLimitReached) return;
    setEditItem(null);
    setIsSidePanelOpen(true);
  };

  const handleEditClick = (item: CustomKnowledgeRecord) => {
    setEditItem(item);
    setIsSidePanelOpen(true);
  };

  const handleDeleteClick = (itemId: string) => {
    mutateDraft(locationId, (draft) => {
      if (draft.records) {
        draft.records = draft.records.filter((item) => item.id !== itemId);
      }
    });
  };

  const isItemUnsaved = (item: CustomKnowledgeRecord) => {
    const initialItem = initialList.find((init) => init.id === item.id);
    return !initialItem || !isEqual(initialItem, item);
  };

  const disabledHoverLabel = t(
    'You may only have {{count}} notes in your custom knowledge base. Please edit or delete a note to add more information.',
    { count: MAX_CUSTOM_KNOWLEDGE_ITEMS }
  );

  return (
    <div css={styles.container}>
      {customKnowledgeList.length > 0 && (
        <div css={styles.list}>
          {customKnowledgeList.map((item) => {
            const unsaved = isItemUnsaved(item);
            return (
              <KnowledgeFormSection
                key={item.id}
                title={item.customKnowledgeTitle || ''}
                description={
                  item.updatedBy
                    ? t('Custom knowledge updated by {{userName}}', {
                        userName: usersMap[item.updatedBy] || item.updatedBy,
                      })
                    : undefined
                }
                isDirty={unsaved}
                onEdit={() => handleEditClick(item)}
                onDelete={() => item.id && handleDeleteClick(item.id)}
              >
                <Text size='medium' css={styles.description}>
                  {item.customKnowledgeContent || ''}
                </Text>
              </KnowledgeFormSection>
            );
          })}
        </div>
      )}
      <div>
        <Button
          variant='tertiary'
          size='small'
          onClick={handleAddClick}
          disabled={isLimitReached}
          iconName='plus-small'
          hoverLabel={isLimitReached ? disabledHoverLabel : undefined}
          showLabelWhenDisabled
        >
          {t('Add Custom Knowledge')}
        </Button>
      </div>

      <CustomKnowledgeSidePanel
        show={isSidePanelOpen}
        onClose={() => {
          setIsSidePanelOpen(false);
          setEditItem(null);
        }}
        editItem={editItem}
        locationId={locationId}
      />
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-md);
  `,
  header: css`
    display: flex;
    justify-content: flex-start;
  `,
  list: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-md);
  `,
  description: css`
    white-space: pre-wrap;
    word-break: break-word;
    color: var(--fabric-color-text-default);
  `,
};
